/**
 * Violation-remediation derivations, MATTER GRAIN (PR-2, FR-1..FR-4). Board
 * half: `boards.ts`; `index.ts` re-exports both, so 27 importers keep the bare
 * specifier. Atoms: `violationRemediationUI.tsx` (components only, the
 * fast-refresh rule).
 *
 * TRAP: the cut is ONE-DIRECTIONAL. The board half references 20 names from
 * here, this half ZERO from it, and `.eslintrc.cjs` has no `import/no-cycle`
 * rule. `formatDateSpan`, `formatYearSpan`, `mostCommon` and `uniqueShort` are
 * exported only to cross that boundary.
 *
 * A row with no matter gets no augmentation, so a flag-off build (matters
 * query 404s → empty list) is a pure no-op.
 *
 * DA-R12 / PR-2 gate: every requirements profile ships
 * `verification: "unverified"` today, so FR-4 renders "under review" for ALL
 * rows until primary-source verification flips codes to `"verified"`. FR-1's
 * window strings are VERBATIM from PRD FR-1 (counsel-gated, em-dash U+2014).
 */
import type {
  ViolationMatter,
  ViolationLifecycleStatus,
  ViolationCertificationStatus,
  ViolationRespondentProvenance,
  AttributionPosture,
  AttributionSplit,
} from "@/queries/violationMattersQueries";
import "./deadlineWindow";
import { formatDay } from "@/components/portal/shortDay";
// ---------------------------------------------------------------------------
// Statute constants (48 RCNY 6-21, CO §1 verified current text). Agency-wide
// OATH windows, NOT per-code facts, so safe as FE defaults.
//
// TRAP (one formula per user-visible number): the FE computes NO window date.
// Both reopen windows come from the BE (`oath_windows.shown_window`, the Action
// plan's) as `matter.deadlineWindow` and `matter.nextDeadlineAt` (10-25).
// ---------------------------------------------------------------------------

/** Adjudicated-decision appeal window (30d, DA-R8), day-count copy only. */
export const APPEAL_WINDOW_DAYS = 30;

const PENALTY_OPEN_LIFECYCLES: ReadonlySet<ViolationLifecycleStatus> = new Set([
  "OPEN",
  "DEFAULTED",
  "ADJUDICATED_OWING",
  "STIPULATED_PARTIAL",
]);

/**
 * The four terminal lifecycles. EXPORTED BECAUSE THE RECORD TAB NEEDS THE SAME
 * SET (`recordBoard.ts` mutes terminal rows, `deriveClosedHistory` picks the
 * DECIDED table's): a fifth terminal status landing in one list and not the
 * other renders a settled matter un-muted, in risk-red, under a heading
 * "Decided in 2026".
 */
export const TERMINAL_LIFECYCLES: ReadonlySet<ViolationLifecycleStatus> =
  new Set([
    "RESOLVED_PAID",
    "RESOLVED_DISMISSED",
    "WRITTEN_OFF",
    "NOT_APPLICABLE",
  ]);

/** Terminal plus not-yet-classified. Suppresses BOTH the reopen/appeal clock
 *  AND the FR-2 cluster affordance (QA-S2). */
const isNonActionableLifecycle = (lc: ViolationLifecycleStatus): boolean =>
  TERMINAL_LIFECYCLES.has(lc) || lc === "UNCLASSIFIED";

// ---------------------------------------------------------------------------
// Date helpers. Date-only ISO ("YYYY-MM-DD") parses as UTC midnight; compute
// and format in UTC so a window boundary never drifts a day in Eastern (the
// `dayShortFromISO` lesson from AttorneyConsultLifecycle).
// ---------------------------------------------------------------------------

export const parseDateOnly = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const d = new Date(iso.length <= 10 ? `${iso}T12:00:00Z` : iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "YYYY-MM-DD" in New York, the portal's today: a deadline lapses once its day ends there (Leo 10-12). */
export const nyDate = (at: Date = new Date()): string =>
  at.toLocaleDateString("en-CA", { timeZone: "America/New_York" });

/** "Aug 23, 2026", firm day-level window close. */
export const formatClockDate = (d: Date): string =>
  d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

/** "Mar 2027", year-scale discretionary window (deliberately fuzzy). */
export const formatClockMonthYear = (d: Date): string =>
  d.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

/** "03/24/2020", the citation document's OWN date idiom (PR-C3 / CIT-14). An
 *  extract-sourced compliance date renders as printed on the summons,
 *  day-level, DISTINCT from the heuristic's coarse `formatClockMonthYear`. */
export const formatCitationDate = (d: Date): string =>
  d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });

/** Best "when did this happen" anchor: mailed date, else feed decision date,
 *  else hearing date. The SINGLE precedence, reused by the cert-overdue
 *  heuristic (FIX-1), the posture-bucket date spans and the closed-history
 *  year range. */
export const matterAnchorDate = (
  matter: Pick<
    ViolationMatter,
    "decisionMailedDate" | "decisionDate" | "hearingDate"
  >,
): Date | null =>
  parseDateOnly(matter.decisionMailedDate) ??
  parseDateOnly(matter.decisionDate) ??
  parseDateOnly(matter.hearingDate);

// ---------------------------------------------------------------------------
// FR-1: reopen/appeal-clock sub-label taxonomy, from `lifecycleStatus` + the
// decision-date basis (PRD FR-1 / CO-R2: `hearing_date` is NEVER the anchor,
// the mailed decision date is). The `~` prefix marks a `decisionDate` estimate
// proxy and drops once `decisionMailedDate` firms it.
// ---------------------------------------------------------------------------

export type ClockTone = "urgent" | "caution" | "muted";

/** `text` is the human string, `kind` the enum `classifyPostureBucket` (PR-V2)
 *  reads, so the bucket math never re-implements the window logic. */
export type ReopenClockKind =
  | "appeal_window" // ADJUDICATED_OWING, 30/35d appeal track
  | "reopened_appear" // DEFAULTED + future (vacated/re-scheduled) hearing
  | "reopened_awaiting_result" // RESCHEDULED/DEFAULTED, hearing passed, no result
  | "reopen_near_automatic" // DEFAULTED, first-request auto-grant window open
  | "reopen_with_excuse" // DEFAULTED, past auto-grant, ≤1yr discretionary window
  | "reopen_passed" // DEFAULTED, past both windows, settle/pay
  | "default_no_date" // DEFAULTED, no decision basis on the wire to date it
  | "hearing_contest_open"; // pre-adjudication (SCHEDULED/OPEN) + future hearing

export interface ReopenClockLabel {
  text: string;
  tone: ClockTone;
  /** true when the date derives from `decisionDate` (estimate → `~`). */
  estimate: boolean;
  /** The category discriminant (see `ReopenClockKind`). */
  kind: ReopenClockKind;
}

export interface ReopenWindow {
  /** The 1-year Reason by date (`deadlineWindow.reasonBy`); null once passed. */
  at: Date | null;
  /** Basis was `decisionDate`, a ~7-day proxy → callers keep the `~`. */
  estimate: boolean;
  passed: boolean;
}

/**
 * The 1-year reopen window as a VALUE, read off the BE's `deadlineWindow`
 * (the Exposure board groups dollars by it). null when the BE gives no reopen
 * window: no default, or no decision date to count from.
 */
export function deriveReopenWindow(
  matter: Pick<ViolationMatter, "decisionMailedDate" | "decisionDate" | "deadlineWindow">,
): ReopenWindow | null {
  const w = matter.deadlineWindow;
  if (!w || w.leg === "appeal") return null;
  const at = parseDateOnly(w.reasonBy);
  if (!at && w.leg !== "closed") return null;
  return {
    at,
    estimate: !matter.decisionMailedDate && !!matter.decisionDate,
    passed: w.leg === "closed",
  };
}

/**
 * FR-1 sub-label copy, the SINGLE source of truth for the counsel-gated
 * reopen/appeal taxonomy (em-dash U+2014). `deriveReopenClockLabel` AND its
 * unit tests read these templates, so a wrong word cannot slip through by
 * being duplicated identically in source + test.
 */
export const REOPEN_CLOCK_COPY = {
  /** PR-4's copy source (FR-14), a SEPARATE row visual, not the FR-1 clock. */
  needsTriage: (respondent: string): string =>
    `RESPONDENT: ${respondent}. UNDER REVIEW`,
  /** ADJUDICATED_OWING, the 30/35-day OATH appeal track (day count). */
  appealWindow: (days: number): string =>
    `APPEAL WINDOW. ${days} DAYS FROM DECISION`,
  /** DEFAULTED with a future (vacated + re-scheduled) hearing. */
  reopenedAppear: (hearing: string): string =>
    `REOPENED. APPEAR ${hearing} OR IT RE-DEFAULTS`,
  /** Reopened, rescheduled hearing passed, no new result on the record. */
  reopenedAwaitingResult: (hearing: string): string =>
    `REOPENED. HEARING ${hearing} HELD, AWAITING RESULT`,
  /** DEFAULTED, auto-grant window open. `deadline` = `nextDeadlineAt`. */
  reopenCloses: (deadline: string, estimate: boolean): string =>
    `REOPEN CLOSES ${estimate ? "~" : ""}${deadline}`,
  /** Past the plan's safe date, inside the law's: the BE's label verbatim, "May have closed. Ask by". */
  reopenMayHaveClosed: (label: string, deadline: string): string =>
    `${label} ${deadline}`,
  /** DEFAULTED, past auto-grant but within the 1yr discretionary window. */
  reopenDiscretionary: (windowEnd: string, estimate: boolean): string =>
    `DEFAULT ON RECORD. REOPEN IF GRANTED, W/ EXCUSE, TO ${estimate ? "~" : ""}${windowEnd}`,
  /** DEFAULTED, past the 1yr discretionary window. */
  reopenPassed: "REOPEN WINDOW PASSED. SETTLE, PAY, OR ASK ABOUT EXCEPTIONS",
  // TODO(counsel-copy · QA-S1): an 8th string beyond the PRD §6 taxonomy,
  // which enumerates only the `…, TO ~<date>` form. Include it in the FR-1
  // counsel copy pass.
  /** DEFAULTED, on record but no decision basis on the wire to date it. */
  defaultNoDate: "DEFAULT ON RECORD. REOPEN IF GRANTED, W/ EXCUSE",
  /** Pre-adjudication (SCHEDULED/OPEN) with a future hearing. */
  hearingContestOpen: (hearing: string): string =>
    `HEARING ${hearing}. CONTEST WINDOW OPEN`,
} as const;

/** jz4z-kudi RESCHEDULED + DEFAULTED: the default was reopened (VA-14, RC18).
 *  BE twin `oath_status_label.is_reopened_after_default`. The BE lifecycle
 *  reads SCHEDULED, then OPEN once the hearing passes (Q14), so this is
 *  checked before the lifecycle switch. */
export const reopenedAfterDefault = (
  m: Pick<ViolationMatter, "hearingStatus" | "hearingResult">,
): boolean =>
  (m.hearingStatus ?? "").trim().toUpperCase() === "RESCHEDULED" &&
  ["DEFAULTED", "DEFAULT"].includes(
    (m.hearingResult ?? "").trim().toUpperCase(),
  );

/** BE `oath_status_label.AWAITING_HEARING_STATUSES`; pinned by a parity test. */
export const AWAITING_HEARING_STATUSES: ReadonlySet<string> = new Set([
  "NEW ISSUANCE",
  "RESCHEDULED",
  "ASSIGNED",
  "HRG BEFORE ASSIGN",
]);

/** BE twin `oath_status_label.is_past_hearing_awaiting_result` (Q14b): the
 *  hearing day is behind New York's today and no result posted. */
export const pastHearingAwaitingResult = (
  m: Pick<ViolationMatter, "hearingStatus" | "hearingResult" | "hearingDate">,
  now: Date = new Date(),
): boolean => {
  const hearing = (m.hearingDate ?? "").slice(0, 10);
  const today = nyDate(now);
  if (!hearing || hearing >= today) return false;
  const result = (m.hearingResult ?? "").trim().toUpperCase();
  const status = (m.hearingStatus ?? "").trim().toUpperCase();
  return (
    reopenedAfterDefault(m) ||
    (["", "NONE"].includes(result) && AWAITING_HEARING_STATUSES.has(status))
  );
};

export function deriveReopenClockLabel(
  matter: ViolationMatter,
  now: Date = new Date(),
): ReopenClockLabel | null {
  const lifecycle = matter.lifecycleStatus;

  // Terminal / unclassified matters carry no live clock.
  if (isNonActionableLifecycle(lifecycle)) {
    return null;
  }

  if (reopenedAfterDefault(matter)) {
    const hearing = parseDateOnly(matter.hearingDate);
    if (!hearing || hearing.getTime() > now.getTime()) {
      return hearing
        ? {
            text: REOPEN_CLOCK_COPY.reopenedAppear(formatClockDate(hearing)),
            tone: "urgent",
            estimate: false,
            kind: "reopened_appear",
          }
        : null;
    }
    return {
      text: REOPEN_CLOCK_COPY.reopenedAwaitingResult(formatClockDate(hearing)),
      tone: "caution",
      estimate: false,
      kind: "reopened_awaiting_result",
    };
  }

  // TRAP: the clock is attribution-INDEPENDENT. Every matter defaults to
  // NEEDS_TRIAGE until PR-4 triages attribution, so gating on attribution
  // masked it on EVERY row.

  // `~` when the date counts from the decision-date proxy, not a mailed date.
  const window = deriveReopenWindow(matter);
  const estimate = window?.estimate ?? false;

  // Adjudicated-owing → the 30/35-day OATH appeal track (DA-R8). A day-count
  // label, not a date, so no `~` and no `nextDeadlineAt` read: the window
  // LENGTH is the raw profile field, not a computed number.
  if (lifecycle === "ADJUDICATED_OWING") {
    const days =
      matter.requirementsProfile?.appealWindowDays ?? APPEAL_WINDOW_DAYS;
    return {
      text: REOPEN_CLOCK_COPY.appealWindow(days),
      tone: "caution",
      estimate: false,
      kind: "appeal_window",
    };
  }

  if (lifecycle === "DEFAULTED") {
    // A future hearing on a defaulted matter = the default was vacated and
    // re-scheduled; they must appear or it re-defaults (CO-R3 real rows).
    const hearing = parseDateOnly(matter.hearingDate);
    if (hearing && hearing.getTime() > now.getTime()) {
      return {
        text: REOPEN_CLOCK_COPY.reopenedAppear(formatClockDate(hearing)),
        tone: "urgent",
        estimate: false,
        kind: "reopened_appear",
      };
    }
    // The BE's leg and date, the Action plan's (`deadlineWindow`); BE twin
    // `violation_partitions.derive_reopen_clock_kind` reads the same leg.
    const leg = matter.deadlineWindow?.leg;
    const by = parseDateOnly(matter.deadlineWindow?.by ?? null);
    const kind = leg === "75d" ? "reopen_near_automatic" : "reopen_with_excuse";
    if (by && matter.deadlineWindow?.maybeClosed) {
      return {
        text: REOPEN_CLOCK_COPY.reopenMayHaveClosed(
          matter.deadlineWindow.label ?? "",
          formatDay(matter.deadlineWindow.by as string, nyDate(now)),
        ),
        tone: "urgent",
        estimate: false,
        kind,
      };
    }
    if (by && leg === "75d") {
      return {
        text: REOPEN_CLOCK_COPY.reopenCloses(formatClockDate(by), estimate),
        tone: "urgent",
        estimate,
        kind,
      };
    }
    if (by && leg === "1y") {
      return {
        text: REOPEN_CLOCK_COPY.reopenDiscretionary(formatClockMonthYear(by), estimate),
        tone: "caution",
        estimate,
        kind,
      };
    }
    if (leg === "closed") {
      return {
        text: REOPEN_CLOCK_COPY.reopenPassed,
        tone: "muted",
        estimate: false,
        kind: "reopen_passed",
      };
    }
    // Defaulted, no decision basis on the wire: the default is on record but
    // the window cannot be dated, so drop the "TO ~<date>" tail.
    return {
      text: REOPEN_CLOCK_COPY.defaultNoDate,
      tone: "caution",
      estimate: false,
      kind: "default_no_date",
    };
  }

  // Pre-adjudication (SCHEDULED / OPEN) with a future hearing: contest window
  // open, a firm city date, no `~`.
  // TODO(QA-S3): OPEN / STIPULATED_PARTIAL also fall through here, and a
  // STIPULATED_PARTIAL (settlement entered) reading "CONTEST WINDOW OPEN" is
  // questionable. Confirm both with counsel before verified-copy rollout.
  const hearing = parseDateOnly(matter.hearingDate);
  if (hearing && hearing.getTime() > now.getTime()) {
    return {
      text: REOPEN_CLOCK_COPY.hearingContestOpen(formatClockDate(hearing)),
      tone: "urgent",
      estimate: false,
      kind: "hearing_contest_open",
    };
  }

  return null;
}

// ---------------------------------------------------------------------------
// FR-3: two orthogonal axes (penalty · certification). Sourced from the
// lifecycle + the city's own `certification_status` (never guessed).
// ---------------------------------------------------------------------------

export type PenaltyAxis = "open" | "held" | "scheduled" | "closed";
export type CertAxis =
  | "accepted"
  | "due"
  | "overdue"
  | "not_required"
  | "unknown";

export interface TwoAxisDotsState {
  penalty: PenaltyAxis;
  certification: CertAxis;
}

const CERT_AXIS_BY_STATUS: Record<ViolationCertificationStatus, CertAxis> = {
  ACCEPTED: "accepted",
  DUE: "due",
  NOT_REQUIRED: "not_required",
  UNKNOWN: "unknown",
  // FILED is staff/owner-attested pending city acceptance, treat as
  // "in flight / under review" visually, never as certified.
  FILED: "unknown",
};

// ---------------------------------------------------------------------------
// FIX-1: certify "OVERDUE" (feed-only, agency-wide). An FDNY
// Order-to-Correct requires a certificate of correction within 35 days, so a
// years-old defaulted matter reading "not yet certified" understates the
// truth. Feed-date-driven, so it ignores the profile verification gate:
//
//   overdue ⟺ an OPEN certification obligation (certificationStatus === "DUE",
//             never ACCEPTED/FILED/NOT_REQUIRED) whose best anchor date
//             (mailed decision → feed decision → hearing) is older than the
//             safe cure margin.
//
// SAFE MARGIN = 182 days (~6 months), > 5× the 35-day FDNY window, so it never
// fires on a fresh obligation or one we cannot date. The exact 35-day
// compliance date is never fabricated (a deferred OCR item); "since" is the
// anchor's coarse month-year. An OPERATIONAL state, not legal advice.
// ---------------------------------------------------------------------------

/** Safe cure margin (days). A cert obligation anchored older than this is
 *  treated as lapsed. ~6 months, > 5× the 35-day FDNY certify window, so it
 *  never false-positives on a still-curable obligation. */
export const CERT_OVERDUE_SAFE_MARGIN_DAYS = 182;

const DAY_MS = 86_400_000;

export interface CertOverdueState {
  /** true → an open cert obligation whose certify window has demonstrably lapsed. */
  overdue: boolean;
  /** Coarse "overdue since" month-year (from the anchor date) when datable; null
   *  when overdue cannot be dated (never fabricated). */
  sinceLabel: string | null;
}

/**
 * The SINGLE cert-overdue formula, read by BOTH `deriveTwoAxisDots` and
 * `deriveCertDueLabel`, so the dots and the words can never disagree.
 */
export function deriveCertOverdue(
  matter: Pick<
    ViolationMatter,
    | "certificationStatus"
    | "decisionMailedDate"
    | "decisionDate"
    | "hearingDate"
  > &
    Partial<
      Pick<
        ViolationMatter,
        "issuingAgency" | "issuingAgencyCode" | "requirementsProfile"
      >
    >,
  now: Date = new Date(),
): CertOverdueState {
  // Only an OPEN obligation can be overdue. ACCEPTED = done, FILED =
  // owner-attested/in-flight, NOT_REQUIRED/UNKNOWN = no live obligation.
  if (matter.certificationStatus !== "DUE") {
    return { overdue: false, sinceLabel: null };
  }
  // F2, the DOB carve-out. A DOB certification has NO deadline: the COC is
  // accepted ANY time and the boxed CURE DATE is a penalty-WAIVER window, not
  // a cert due date (the dob_cure_window relabel), so "overdue" is a CATEGORY
  // ERROR for DOB. Without this, list rows shouted a red "Certify — overdue
  // since <month>" while the drawer said "cure window closed … certifiable any
  // time" one click apart. Wire code first; the profile covers a payload
  // persisted before 2026-09-24.
  const profileAgency = (matter.requirementsProfile?.agency || "")
    .trim()
    .toUpperCase();
  const isDob = agencyCode(matter) === "DOB" || profileAgency === "DOB";
  if (isDob) {
    return { overdue: false, sinceLabel: null };
  }
  const anchor = matterAnchorDate(matter);
  if (!anchor) {
    // No datable anchor → the lapse cannot be demonstrated.
    return { overdue: false, sinceLabel: null };
  }
  const ageDays = (now.getTime() - anchor.getTime()) / DAY_MS;
  if (ageDays > CERT_OVERDUE_SAFE_MARGIN_DAYS) {
    return { overdue: true, sinceLabel: formatClockMonthYear(anchor) };
  }
  return { overdue: false, sinceLabel: null };
}

export function deriveTwoAxisDots(
  matter: ViolationMatter,
  now: Date = new Date(),
): TwoAxisDotsState {
  const lc = matter.lifecycleStatus;
  let penalty: PenaltyAxis = "closed";
  if (PENALTY_OPEN_LIFECYCLES.has(lc) && matter.onCityLedger) {
    penalty = "open";
  } else if (lc === "SCHEDULED") {
    // Pre-adjudication, scheduled, not accrued (CO-R3 / principle 5).
    penalty = "scheduled";
  }
  // Q14b: $0 on the record after the hearing is an unknown, never "$0 owed"
  // (014269529H, $1,550 sought, hearing 2026-10-19).
  if (
    penalty !== "closed" &&
    parseUsd(matter.exposureUsd) <= 0 &&
    pastHearingAwaitingResult(matter, now)
  ) {
    penalty = "held";
  }
  let certification =
    CERT_AXIS_BY_STATUS[matter.certificationStatus] ?? "unknown";
  // FIX-1 escalation, the SAME formula the row/drawer copy reads.
  if (certification === "due" && deriveCertOverdue(matter, now).overdue) {
    certification = "overdue";
  }
  return { penalty, certification };
}

// ---------------------------------------------------------------------------
// FR-4: "CORRECTION / COMPLIANCE DUE" per-row label. The agency is DERIVED
// from the profile, never hardcoded. Conservative "under review" copy whenever
// verification has not cleared the PR-2 gate or the authority is unknown,
// which is EVERY code today; a lapsed obligation OVERRIDES both (FIX-1).
// ---------------------------------------------------------------------------

export interface CertDueLabel {
  text: string;
  /** true → conservative copy (unverified profile or unknown authority). */
  underReview: boolean;
}

/**
 * "Register" for a verified DEP profile (boiler/emissions codes
 * REGISTER/renew), "Certify" for everything else (§3.2 / CO-R1;
 * agency-specific only once the profile is verified). The ONE formula shared
 * by `deriveCertDueLabel` and the PR-C3 citation-anchored cert axis.
 */
export function certVerbForMatter(
  matter: Pick<ViolationMatter, "requirementsProfile">,
): "Certify" | "Register" {
  const profile = matter.requirementsProfile;
  const verified = profile?.verification === "verified";
  const authority = (profile?.certifyingAuthority || "").trim();
  const agency = (profile?.agency || "").trim().toUpperCase();
  const authorityKnown =
    verified && !!authority && !!agency && agency !== "UNKNOWN";
  return authorityKnown && agency === "DEP" ? "Register" : "Certify";
}

export function deriveCertDueLabel(
  matter: ViolationMatter,
  now: Date = new Date(),
): CertDueLabel {
  const profile = matter.requirementsProfile;
  const verified = profile?.verification === "verified";
  const authority = (profile?.certifyingAuthority || "").trim();
  const agency = (profile?.agency || "").trim().toUpperCase();
  const authorityKnown =
    verified && !!authority && !!agency && agency !== "UNKNOWN";
  const isRegister = certVerbForMatter(matter) === "Register";

  // FIX-1: OVERDUE overrides, ignoring the per-code verification gate like
  // the reopen clock. `underReview` stays false: overdue is a firm state.
  const overdue = deriveCertOverdue(matter, now);
  if (overdue.overdue) {
    const verb = isRegister ? "Register" : "Certify";
    const since = overdue.sinceLabel ? ` since ${overdue.sinceLabel}` : "";
    return { text: `${verb}. Overdue${since}`, underReview: false };
  }

  if (!authorityKnown) {
    return { text: "Correction/compliance. Under review", underReview: true };
  }
  if (isRegister) {
    return {
      text: `Compliance not yet registered with ${authority}`,
      underReview: false,
    };
  }
  return {
    text: `Correction not yet certified with ${authority}`,
    underReview: false,
  };
}

// ---------------------------------------------------------------------------
// Matter ↔ penalty-row join. The persistent key is `ticket_number`, but
// penalty rows carry only the composite `rowId` ("oath-x:012179816Z"). A
// matter serializes BOTH its resolved `rowId` and its `ticketNumber`. Primary
// join = exact `rowId` (the BE already did the twin-dedup); fallback = the
// ticket parsed out of the `rowId` prefix.
// ---------------------------------------------------------------------------

/** Row shape we join against (the penalty-exposure wire row). */
interface JoinableRow {
  rowId?: string;
}

export interface MatterIndex {
  byRowId: Map<string, ViolationMatter>;
  byTicket: Map<string, ViolationMatter>;
}

/** Strip a known composite prefix ("oath-x:", "ecb:", …) → the locator. */
export const ticketFromRowId = (
  rowId: string | null | undefined,
): string | null => {
  if (!rowId) return null;
  const idx = rowId.indexOf(":");
  const locator = (idx >= 0 ? rowId.slice(idx + 1) : rowId).trim();
  return locator || null;
};

export function buildMatterIndex(
  matters: readonly ViolationMatter[],
): MatterIndex {
  const byRowId = new Map<string, ViolationMatter>();
  const byTicket = new Map<string, ViolationMatter>();
  matters.forEach((m) => {
    if (m.rowId) byRowId.set(m.rowId, m);
    if (m.ticketNumber) byTicket.set(m.ticketNumber.trim(), m);
  });
  return { byRowId, byTicket };
}

export function matterForRow(
  index: MatterIndex,
  row: JoinableRow,
): ViolationMatter | null {
  if (row.rowId) {
    const exact = index.byRowId.get(row.rowId);
    if (exact) return exact;
    const ticket = ticketFromRowId(row.rowId);
    if (ticket) {
      const byTicket = index.byTicket.get(ticket);
      if (byTicket) return byTicket;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// FR-2: same-charge clusters. Matters sharing a `chargeCode`; ≥2 rolls up to
// a pattern affordance, labelled honestly ("likely one underlying system;
// confirm") until staff/owner confirm (DA-S2).
// ---------------------------------------------------------------------------

export interface ChargeCluster {
  chargeCode: string;
  matters: ViolationMatter[];
}

/** A pattern worth a get-help affordance is two or more live tickets on one
 *  charge; a lone ticket is not a pattern. */
export const CLUSTER_MIN_SIZE = 2;

/**
 * Group live matters by charge code. `minSize` EXISTS FOR THE EXPOSURE BOARD'S
 * "BY PATTERN" TABLE, which lists EVERY live charge group including
 * single-ticket ones (BF20 with one, AX25 with one, beside BF35 with five); a
 * local `reduce` there would be a SECOND grouping that drifts from this one.
 * Every existing caller takes the default and sees only real patterns.
 */
export function clusterMattersByChargeCode(
  matters: readonly ViolationMatter[],
  minSize: number = CLUSTER_MIN_SIZE,
): ChargeCluster[] {
  const groups = new Map<string, ViolationMatter[]>();
  matters.forEach((m) => {
    // QA-S2: terminal / unclassified matters carry no live action and must
    // never surface a "Get help" cluster, so a charge whose only shared
    // matters are resolved drops out of clustering entirely.
    if (isNonActionableLifecycle(m.lifecycleStatus)) return;
    const code = (m.chargeCode || "").trim();
    if (!code) return;
    const bucket = groups.get(code);
    if (bucket) bucket.push(m);
    else groups.set(code, [m]);
  });
  return Array.from(groups.entries())
    .filter(([, ms]) => ms.length >= minSize)
    .map(([chargeCode, ms]) => ({ chargeCode, matters: ms }))
    .sort((a, b) => b.matters.length - a.matters.length);
}

// ===========================================================================
// FR-14 / FR-15: attribution triage (PR-4). The BE owns the split math
// (`services_violation_attribution.py`); the FE READS `attributionSplit` and
// does ONE local computation, the optimistic re-bucket, which MIRRORS the
// server partition exactly.
// ===========================================================================

/** Parse a wire USD Decimal string ("117075.00" / "1,580" / "$5000") → a
 *  finite number. Non-numeric / null → 0. */
export const parseUsd = (raw: string | null | undefined): number => {
  if (raw == null) return 0;
  const n = Number(String(raw).replace(/[$,]/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

/** Full-precision USD ("$117,075"). The decomposition chips show WHOLE
 *  dollars, not the hero's "$117.1k": the exclusion figure must be exact so an
 *  owner can reconcile it against the ledger rows. */
export const formatUsdFull = (value: number): string =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

/** BE `prehearing_charge` (audit RC17): a $0 ticket awaiting its hearing.
 *  Charged, never owed. Not on `ViolationMatter` yet: that type lives in
 *  `queries/`, out of lane. Absent on older payloads. */
export interface PrehearingCharge {
  usd: string;
  hearingDate: string | null;
  dataset: string;
  recordId: string;
  link: string;
}

export const prehearingCharge = (
  matter: ViolationMatter,
): PrehearingCharge | null =>
  (matter as ViolationMatter & { charged?: PrehearingCharge | null }).charged ??
  null;

/** "$1,550 sought at the Oct 19, 2026 hearing. Not owed." */
export const chargedLabel = (charge: PrehearingCharge): string => {
  const hearing = parseDateOnly(charge.hearingDate);
  const usd = formatUsdFull(parseUsd(charge.usd));
  return hearing
    ? `${usd} sought at the ${formatClockDate(hearing)} hearing. Not owed.`
    : `${usd} sought at hearing. Not owed.`;
};

// ---------------------------------------------------------------------------
// Posture → per-row treatment. Driven by the DERIVED `attributionPosture`,
// which separates PRIOR_OWNER_VERIFY (a subset of NEEDS_TRIAGE) from plain
// UNDER_REVIEW; the `attribution` field alone cannot.
// ---------------------------------------------------------------------------

export type AttributionTreatmentKind =
  | "owner" // OWNER_SIDE, unchanged from PR-2 (no extra chrome)
  | "triage" // UNDER_REVIEW, amber "Whose is this?" one-tap resolve
  | "prior_owner" // PRIOR_OWNER_VERIFY, "predates your ownership — verify" note
  | "excluded"; // NOT_YOURS, de-emphasized "excluded from your total" + undo

const POSTURE_TREATMENT: Record<AttributionPosture, AttributionTreatmentKind> =
  {
    OWNER_SIDE: "owner",
    UNDER_REVIEW: "triage",
    PRIOR_OWNER_VERIFY: "prior_owner",
    NOT_YOURS: "excluded",
  };

/** Prefers the BE-derived posture; the `attribution` fallback is defensive
 *  against a future BE enum addition. */
export function attributionTreatmentForMatter(
  matter: ViolationMatter,
): AttributionTreatmentKind {
  const byPosture = POSTURE_TREATMENT[matter.attributionPosture];
  if (byPosture) return byPosture;
  switch (matter.attribution) {
    case "OWNER":
      return "owner";
    case "TENANT_OR_THIRD_PARTY":
      return "excluded";
    default:
      return matter.respondentVerifyRequired ? "prior_owner" : "triage";
  }
}

/** $0 unless `countsInAttributionSplit`, else the already `max(0,·)`-floored
 *  `score_exposure_usd`. A TERMINAL matter contributes $0 even though
 *  `serialize_matter` gives it a closed-composite `exposureUsd`. */
export function matterSplitContributionUsd(matter: ViolationMatter): number {
  if (!countsInAttributionSplit(matter)) return 0;
  return Math.max(0, parseUsd(matter.exposureUsd));
}

/**
 * IS THIS MATTER IN THE SERVER'S SPLIT AT ALL: the membership predicate, in
 * the one place it is spelled.
 *
 * The BE resolves EVERY matter against the OPEN composite and skips rows it
 * cannot find (`attribution_split_for_matters`: `if resolve_matter_row(m,
 * open_composite) is None: continue`). Two populations fall through: a
 * TERMINAL matter (serialized against the CLOSED composite, so it has an
 * `exposureUsd` but no open row) and a non-terminal row that left the city
 * ledger (CTO-6). Neither contributes dollars AND neither contributes a COUNT.
 *
 * EXPORTED, NOT RE-FILTERED PER CALLSITE: three surfaces print something
 * beside a server-computed number (the optimistic re-bucket, the Exposure
 * board's named "not yours" tickets, the sentence saying why), and a
 * disagreeing filter puts a LIST and a COUNT of different lengths on the
 * legally load-bearing "this money is not yours" line, the panel says "1
 * ticket" and names four.
 */
export function countsInAttributionSplit(matter: ViolationMatter): boolean {
  if (TERMINAL_LIFECYCLES.has(matter.lifecycleStatus)) return false;
  return matter.onCityLedger;
}

export type ExposureBucket = NonNullable<ViolationMatter["bucket"]>;

/** Buckets that owe $0 on this building's row: one wording on the Record and Exposure tabs. */
export const ZERO_OWED_NOTE: Partial<Record<ExposureBucket, string>> = {
  not_yours: "Not yours, $0 owed",
  counted_elsewhere: "Counted at another building, $0 here",
  corrected: "Corrected, $0 owed",
};

/** The matter's dollars inside `enforcement_exposure`: the server's `headline_usd` (lane M 4d91bc5d).
 *  Null only at one_number v1, where the split contribution is the figure. */
export const headlineShareUsd = (matter: ViolationMatter): number =>
  matter.headlineUsd == null
    ? matterSplitContributionUsd(matter)
    : parseUsd(matter.headlineUsd);

// ---------------------------------------------------------------------------
// Hero decomposition, DECOMPOSE (never replace) the owed number.
// ---------------------------------------------------------------------------

/**
 * Whole-number percentages of `total` across `parts`, summing to EXACTLY 100
 * when `total > 0`, largest-remainder (Hamilton): floor every share, hand the
 * leftover points to the largest fractional remainders, because three
 * independent `Math.round`s render "80% · 12% · 8%" as 101%. A negative
 * leftover (a wire where `sum(parts) !== total`) is reclaimed from the
 * smallest remainders, so the result is always a valid 0..100 distribution.
 */
export function apportionPercents(parts: number[], total: number): number[] {
  if (!(total > 0)) return parts.map(() => 0);
  const raw = parts.map((p) => (Math.max(0, p) / total) * 100);
  const out = raw.map((r) => Math.floor(r));
  let leftover = 100 - out.reduce((a, b) => a + b, 0);
  const byFrac = raw
    .map((r, i) => ({ i, frac: r - Math.floor(r) }))
    .sort((a, b) => b.frac - a.frac);
  // Positive leftover → +1 to the largest remainders.
  for (let k = 0; leftover > 0 && byFrac.length > 0; k += 1) {
    out[byFrac[k % byFrac.length].i] += 1;
    leftover -= 1;
  }
  // Negative leftover (non-invariant wire only) → reclaim from the smallest
  // remainders, never dropping a share below zero.
  for (let k = byFrac.length - 1; leftover < 0 && k >= 0; k -= 1) {
    if (out[byFrac[k].i] > 0) {
      out[byFrac[k].i] -= 1;
      leftover += 1;
    }
  }
  return out;
}

export interface AttributionDecomposition {
  /** yourEntities + underReview, STAYS in the owed total. */
  owedInHero: number;
  yourEntities: number;
  yourEntitiesCount: number;
  underReview: number;
  underReviewCount: number;
  /** Confirmed third-party, set aside, reversible (LEAVES the owed total). */
  notYours: number;
  notYoursCount: number;
  /** yourEntities + underReview + notYours, the full OATH subset partitioned. */
  compositeOathOwed: number;
  /** Whole-number % of compositeOathOwed, apportioned so the three sum to 100
   *  (the FR-14 "80 / 12 / 8" split). The render reads these verbatim. */
  yourEntitiesPct: number;
  underReviewPct: number;
  notYoursPct: number;
  /** true when there IS a not-yours exclusion to surface. */
  hasNotYours: boolean;
  /** true when there is anything worth decomposing: a not-yours exclusion OR
   *  under-review money to distinguish from plain owner money. */
  hasDecomposition: boolean;
}

/** The hero decomposition numbers. NO recomputation of the dollars; null with
 *  no split. */
export function deriveAttributionDecomposition(
  split: AttributionSplit | null | undefined,
): AttributionDecomposition | null {
  if (!split) return null;
  const yourEntities = parseUsd(split.yourEntities.amount);
  const underReview = parseUsd(split.underReview.amount);
  const notYours = parseUsd(split.notYours.amount);
  // READ, NOT SUMMED: both totals ship on the split, and summing them in the
  // browser is two formulas for one number.
  const wire = split as AttributionSplit & {
    owedTotal?: string;
    compositeOathOwed?: string;
    shares?: {
      pctOfComposite?: {
        yourEntities?: number;
        underReview?: number;
        notYours?: number;
      };
    };
  };
  const owedInHero =
    wire.owedTotal === undefined
      ? yourEntities + underReview
      : parseUsd(wire.owedTotal);
  const compositeOathOwed =
    wire.compositeOathOwed === undefined
      ? owedInHero + notYours
      : parseUsd(wire.compositeOathOwed);
  const hasNotYours = notYours > 0 && split.notYours.count > 0;
  const hasDecomposition =
    compositeOathOwed > 0 && (hasNotYours || underReview > 0);
  // Apportioned SERVER-SIDE (`_stamp_shares`): three integers summing to 100.
  // Rounding each share in the browser prints 101% (37.5 + 62.5 -> 38 + 63).
  // The fallback is for a payload persisted before the field shipped.
  const of = wire.shares?.pctOfComposite;
  const [yourEntitiesPct, notYoursPct, underReviewPct] =
    of?.yourEntities === undefined
      ? apportionPercents([yourEntities, notYours, underReview], compositeOathOwed)
      : [of.yourEntities, of.notYours ?? 0, of.underReview ?? 0];
  return {
    owedInHero,
    yourEntities,
    yourEntitiesCount: split.yourEntities.count,
    underReview,
    underReviewCount: split.underReview.count,
    notYours,
    notYoursCount: split.notYours.count,
    compositeOathOwed,
    yourEntitiesPct,
    underReviewPct,
    notYoursPct,
    hasNotYours,
    hasDecomposition,
  };
}

export const uniqueShort = (
  values: ReadonlyArray<string | null | undefined>,
  max: number,
): string[] => {
  const seen = new Set<string>();
  return values.reduce<string[]>((out, v) => {
    if (out.length >= max) return out;
    const s = (v ?? "").trim();
    if (!s || seen.has(s.toLowerCase())) return out;
    seen.add(s.toLowerCase());
    out.push(s);
    return out;
  }, []);
};

/**
 * Plain-English WHY for the not-yours exclusion, DERIVED from the excluded
 * matters' real descriptions. Null when nothing is excluded.
 *
 * NEVER asserts the respondent is a "named individual": NOT_YOURS also covers
 * a CONFIRMED commercial-tenant entity ("MR HAPPY DELI CORP", the BE
 * `classify_respondent` roster-hit path).
 *
 * SCOPED TO THE SPLIT'S POPULATION (`countsInAttributionSplit`) because the
 * sentence renders beside the server's NOT-YOURS BUCKET. A building whose only
 * not-yours ticket was dismissed last year would otherwise print "Attributed
 * to tenants or third parties…" under a panel with no Not-yours column.
 */
export function describeNotYours(
  matters: readonly ViolationMatter[],
): string | null {
  const excluded = matters.filter(
    (m) =>
      countsInAttributionSplit(m) &&
      attributionTreatmentForMatter(m) === "excluded",
  );
  if (excluded.length === 0) return null;
  const kinds = uniqueShort(
    excluded.map((m) => m.description),
    2,
  );
  const kindPhrase = kinds.length ? `. ${kinds.join(", ")}` : "";
  return `Attributed to tenants or third parties, not your entities${kindPhrase}.`;
}

// ===========================================================================
// FIX-2: respondent-of-record STANDING (filing-party surfacing only).
//
// Some OATH tickets name a DISTINCT legal entity as respondent of record: a
// condominium BOARD OF MANAGERS (classified MATCHED_AGENT) or a NYC agency
// (HPD). A 48 RCNY reopen/contest MUST be filed in the NAME OF THE RESPONDENT
// OF RECORD; the owner's name risks rejection for the wrong party.
//
// STANDING ONLY: it NEVER touches the $ attribution (MATCHED_AGENT → OWNER
// money stays put; the condo/CAMBA owner attribution is a separate pending
// decision). Detection MIRRORS the BE's structural dictionaries
// (`services_violation_attribution.py` `_GLOBAL_AGENT_RAW` /
// `_GLOBAL_PRIOR_OWNER_RAW`), so the FE never invents an unrecognized party.
// ===========================================================================

export type RespondentEntityKind = "board_of_managers" | "government_agency";

/**
 * NYC agency respondent patterns → short code, mirroring the BE
 * `_GLOBAL_PRIOR_OWNER_RAW` (the HPD family). A city agency is NEVER the
 * private owner's filing entity, so this is name-driven and
 * resolution-INDEPENDENT: standing depends on who is NAMED, not on our verdict
 * (the HPD ticket is NEEDS_TRIAGE/PREDATES today).
 */
const GOV_AGENCY_RESPONDENT_PATTERNS: ReadonlyArray<{
  re: RegExp;
  code: string;
}> = [{ re: /\bHOUSING PRESERVATION\b|\bHPD\b/, code: "HPD" }];

/**
 * Condominium governing-body signal, mirroring the BE `_GLOBAL_AGENT_RAW`
 * (BOARD OF MANAGERS / the singular "MANAGER" feed typo / MGRS / CONDOMINIUM).
 * The HDFC "HOUSING DEVELOPMENT FUND" vehicle is deliberately EXCLUDED: it is
 * the owner's own affordable-housing owning entity, not a distinct governing
 * body, so it raises no filing-standing question.
 */
const CONDO_BOARD_RE = /\bBOARD OF (?:MANAGERS?|MGRS)\b|\bCONDOMINIUM\b/;

export interface RespondentStanding {
  /** The respondent-of-record name, the city's VERBATIM spelling. */
  name: string;
  kind: RespondentEntityKind;
  /** Short code when a government agency ("HPD"); null for a board. */
  agencyCode: string | null;
  /** Editorial entity-type phrase ("a condominium Board of Managers" /
   *  "the city agency HPD") the standing note reads. */
  entityLabel: string;
}

/** Null for a plain owner respondent (MATCHED_OWNER roster entity / owner LLC)
 *  or a ticket naming nobody. */
export function deriveRespondentStanding(
  matter: Pick<ViolationMatter, "respondentName" | "respondentResolution">,
): RespondentStanding | null {
  const name = (matter.respondentName ?? "").trim();
  if (!name) return null;
  const upper = name.toUpperCase();

  // Government agency, name-driven: a city agency is never the private
  // owner's filing entity, whatever the attribution verdict.
  const gov = GOV_AGENCY_RESPONDENT_PATTERNS.find((p) => p.re.test(upper));
  if (gov) {
    return {
      name,
      kind: "government_agency",
      agencyCode: gov.code,
      entityLabel: `the city agency ${gov.code}`,
    };
  }

  // Condominium Board of Managers, the owner's governing body, classified
  // MATCHED_AGENT. Gate on the AGENT resolution so a MATCHED_OWNER condo (the
  // owner's OWN rostered entity, correctly filed in the owner's name) never
  // trips the note. The money stays owner-side either way.
  if (
    CONDO_BOARD_RE.test(upper) &&
    matter.respondentResolution === "MATCHED_AGENT"
  ) {
    return {
      name,
      kind: "board_of_managers",
      agencyCode: null,
      entityLabel: "a condominium Board of Managers",
    };
  }
  return null;
}

/**
 * CIT-15: the plain-owner respondent DOC note, rendered OUTSIDE the standing
 * block. Two gates:
 *
 *   - standing non-null → null. Board/gov matters render provenance INSIDE
 *     the standing note, so the two blocks are mutually exclusive by
 *     construction (the R1 double-render guard).
 *   - no TRUE conflict → null. A verified extract MATCHING the feed name tells
 *     a plain owner nothing. The conflict
 *     boolean is BE-computed (one formula, spacing-artifact-safe).
 */
export function deriveRespondentDocNote(
  standing: RespondentStanding | null,
  provenance: ViolationRespondentProvenance | null | undefined,
): ViolationRespondentProvenance | null {
  if (standing) return null;
  if (!provenance?.respondentConflict) return null;
  return provenance;
}

// ===========================================================================
// PR-V2: building-page aggregates (cluster card, posture buckets, matter-count
// trio, closed history), read by `exposureBoard.ts`. A posture bucket is ONE read of
// `deriveReopenClockLabel` (its `kind`) plus `attributionTreatmentForMatter`,
// so a row's clock sub-label and its bucket cannot disagree.
// ===========================================================================

/** Live = still carries action: not terminal, not the not-yet-classified
 *  bucket. The inverse of `isNonActionableLifecycle`. */
export const isLiveMatter = (matter: ViolationMatter): boolean =>
  !isNonActionableLifecycle(matter.lifecycleStatus);

/**
 * The "act this month" horizon, a rolling near-term window, NOT the calendar
 * month: the spec's own July-29 snapshot counted late-August reopens/hearings
 * as "this month". A strict calendar month degenerates to 0 at month-end even
 * with a $5k reopen closing in three weeks. Exported so the portfolio
 * This-Month strip (screen 0) counts the SAME horizon as this building trio.
 */
export const ACT_SOON_WINDOW_DAYS = 45;

/** true when `iso` is a datable day from today (New York) through `days` ahead.
 *  Past dates excluded: a lapsed window is no longer something to act on. */
export const isActionableWithin = (
  iso: string | null | undefined,
  now: Date,
  days: number,
): boolean => {
  const d = parseDateOnly(iso);
  if (!d) return false;
  const startOfToday = Date.parse(nyDate(now));
  const diffDays = Math.floor((d.getTime() - startOfToday) / 86_400_000);
  return diffDays >= 0 && diffDays <= days;
};

/**
 * FR (trio), "acts this month": a LIVE matter whose next hearing OR
 * reopen/appeal deadline falls within `ACT_SOON_WINDOW_DAYS` of today.
 * Terminal matters never act. `now` is injectable for deterministic tests.
 */
export function isActThisMonth(
  matter: ViolationMatter,
  now: Date = new Date(),
): boolean {
  if (!isLiveMatter(matter)) return false;
  return (
    isActionableWithin(matter.hearingDate, now, ACT_SOON_WINDOW_DAYS) ||
    isActionableWithin(matter.nextDeadlineAt, now, ACT_SOON_WINDOW_DAYS)
  );
}

/**
 * The agency short code OFF THE WIRE, minted by `tracking/constants/agencies.py`;
 * no FE table (Leo 09-23). A payload persisted before 2026-09-24 has no code and
 * prints the city's filing.
 */
export const agencyCode = (
  matter: Partial<Pick<ViolationMatter, "issuingAgency" | "issuingAgencyCode">>,
): string =>
  (matter.issuingAgencyCode || "").trim() || (matter.issuingAgency || "").trim();

/**
 * PR-V4: pull the FDNY Violation-Category short code ("VC12") out of a charge
 * citation ("…Violation Category 12 (VC12); …") for the pattern header
 * sub-label. Null when the citation names none; never fabricated.
 */
export const extractFireCode = (
  citation: string | null | undefined,
): string | null => {
  if (!citation) return null;
  const m = citation.match(/\bVC\s?\d+\b/i);
  return m ? m[0].replace(/\s+/g, "").toUpperCase() : null;
};

export const mostCommon = (
  values: ReadonlyArray<string | null | undefined>,
): string => {
  const counts = new Map<string, number>();
  let best = "";
  let bestN = 0;
  values.forEach((raw) => {
    const v = (raw ?? "").trim();
    if (!v) return;
    const n = (counts.get(v) ?? 0) + 1;
    counts.set(v, n);
    if (n > bestN) {
      bestN = n;
      best = v;
    }
  });
  return best;
};

const anchorDates = (matters: readonly ViolationMatter[]): Date[] =>
  matters
    .map(matterAnchorDate)
    .filter((d): d is Date => d != null)
    .sort((a, b) => a.getTime() - b.getTime());

/** "Jun 9, 2026"; "Dec 21, 2026 – Dec 23, 2026" within 31 days (RC19: AS18
 *  046887766K/046887777X read "Dec 2026 – Dec 2026"); else "Dec 2025 – Mar 2026". */
export const formatDateSpan = (matters: readonly ViolationMatter[]): string => {
  const ds = anchorDates(matters);
  if (ds.length === 0) return "";
  const first = ds[0];
  const last = ds[ds.length - 1];
  if (first.getTime() === last.getTime()) return formatClockDate(first);
  const fmt =
    last.getTime() - first.getTime() <= 31 * 86_400_000
      ? formatClockDate
      : formatClockMonthYear;
  return `${fmt(first)} – ${fmt(last)}`;
};

/** "2023" or "2023–2024" or null. */
export const formatYearSpan = (
  matters: readonly ViolationMatter[],
): string | null => {
  const ds = anchorDates(matters);
  if (ds.length === 0) return null;
  const y0 = ds[0].getUTCFullYear();
  const y1 = ds[ds.length - 1].getUTCFullYear();
  return y0 === y1 ? `${y0}` : `${y0}–${y1}`;
};
